import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, of } from "rxjs";

import * as PurchaseActions from "./purchase.actions";
import { PurchaseService } from "app/modules/transactions/purchase/purchase.service";

/** Extracts the real backend error message from an HttpErrorResponse.
 *
 * Angular's HttpErrorResponse structure:
 *   error.error  = parsed response body  → { statusCode, message, error } from NestJS
 *   error.status = HTTP status code      → 400, 500, etc.
 *   error.statusText                     → always "Unknown Error" in Angular — DO NOT USE
 *   error.message                        → "Http failure response for URL: 400 Bad Request"
 */
function isPurchaseBatchQtyFloorBody(body: any): body is {
  code: string;
  message?: string | string[];
  details?: Record<string, string>;
} {
  return (
    body &&
    typeof body === 'object' &&
    body.code === 'PURCHASE_BATCH_QTY_FLOOR'
  );
}

function extractErrorMessage(error: any, fallback: string): string {
  // 1. NestJS body: error.error is the parsed JSON body
  const body = error?.error;
  if (body) {
    // message can be string or string[] (class-validator sends arrays)
    if (body.message) {
      return Array.isArray(body.message) ? body.message.join(', ') : String(body.message);
    }
    // Some NestJS responses use 'error' field as description
    if (typeof body === 'string') {
      return body;
    }
  }
  // 2. Network / CORS errors have no body — use status code
  if (error?.status === 0) {
    return 'Cannot reach the server. Please check your network connection.';
  }
  if (error?.status) {
    return `Server error (${error.status}). Please try again.`;
  }
  return fallback;
}

@Injectable()
export class PurchaseEffects {
  constructor(
    private actions$: Actions,
    private purchaseService: PurchaseService
  ) { }

  createPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseActions.createPurchase),
      mergeMap((action) =>
        this.purchaseService.createPurchase(action.purchase).pipe(
          map((response) =>
            PurchaseActions.createPurchaseSuccess({ response })
          ),
          catchError((error) =>
            of(PurchaseActions.createPurchaseFailure({
              error: extractErrorMessage(error, 'Failed to create purchase. Please try again.'),
            }))
          )
        )
      )
    )
  );

  loadLastPurchaseDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseActions.loadLastPurchaseDetails),
      mergeMap(({ itemId, supplierId, rowIndex }) =>
        this.purchaseService.getLastPurchaseDetails(itemId, supplierId).pipe(
          map((itemDetails) =>
            PurchaseActions.loadLastPurchaseDetailsSuccess({
              rowIndex,
              itemDetails,
            })
          ),
          catchError((error) =>
            of(
              PurchaseActions.loadLastPurchaseDetailsFailure({
                rowIndex,
                error: error?.message,
              })
            )
          )
        )
      )
    )
  );

  loadPurchaseById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseActions.loadPurchaseById),
      mergeMap(({ purchaseId }) =>
        this.purchaseService.getPurchaseById(purchaseId).pipe(
          map((purchase) =>
            PurchaseActions.loadPurchaseByIdSuccess({ purchase })
          ),
          catchError((error) =>
            of(
              PurchaseActions.loadPurchaseByIdFailure({
                error: error?.message,
              })
            )
          )
        )
      )
    )
  );

  updatePurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PurchaseActions.updatePurchase),
      mergeMap(({ purchaseId, purchase }) =>
        this.purchaseService.updatePurchase(purchaseId, purchase).pipe(
          map((response) =>
            PurchaseActions.updatePurchaseSuccess({ response })
          ),
          catchError((error) => {
            const body = error?.error;
            if (isPurchaseBatchQtyFloorBody(body)) {
              const msg =
                typeof body.message === 'string'
                  ? body.message
                  : Array.isArray(body.message)
                    ? body.message.join(' ')
                    : 'You cannot reduce quantity + free below what has already been used from this batch.';
              return of(
                PurchaseActions.updatePurchaseFailure({
                  error: {
                    kind: 'purchase_batch_qty_floor',
                    message: msg,
                    details: body.details,
                  },
                }),
              );
            }
            return of(
              PurchaseActions.updatePurchaseFailure({
                error: extractErrorMessage(
                  error,
                  'Failed to update purchase. Please try again.',
                ),
              }),
            );
          })
        )
      )
    )
  );
}
